import React, { useState } from "react"

import { useTranslation } from "react-i18next"

import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined"
import AddOutlinedIcon from "@mui/icons-material/AddOutlined"
import { Grid } from "@mui/material"
import { Button, Menu, MenuItem, Select, Typography } from "@synapse-analytics/synapse-ui"
import { FieldArray, useFormikContext } from "formik"
import { v4 as uuidv4 } from "uuid"

import { getTheme } from "../../../hooks/UseTheme"
import type { Rule } from "../../../types/custom/rules"
import { DataBlock } from "../../Ruleset/components/RuleCard"
import { EMPTY_SCORECARD_CONDITION } from "../../ScoreCardSets/Constants"
import { LOGICAL_OPERATORS } from "../Constants"
import type { GroupConditions, GroupItemProps, GroupRendererProps, ScoreCardFormikValues } from "../Interfaces"
import { handleRemoveCondition as handleRemoveConditionByPath } from "../helpers"
import { Condition } from "./Condition"

import styles from "./RuleCard.module.scss"

function GroupItem(props: GroupItemProps): React.ReactElement {
  const {
    group,
    groupIndex,
    ruleIndex,
    parentPath,
    editMode,
    createMode,
    currentDepth,
    maxDepth,
    groupAnchor,
    onSetGroupAnchor,
  } = props

  const { t } = useTranslation("scorecards")
  const theme = getTheme()
  const { values, setFieldValue, setValues, isSubmitting } = useFormikContext<ScoreCardFormikValues>()

  const currentPath = [...parentPath, groupIndex]
  const pathKey = currentPath.join("-")

  const getFieldArrayName = (): string => {
    return `cards[${ruleIndex}].${currentPath
      .map((idx, i) => (i === 0 ? `groups[${idx}]` : `.groups[${idx}]`))
      .join("")}.conditions`
  }

  const getConditionId = (conditionIndex: number): string => {
    return `cards[${ruleIndex}].${currentPath
      .map((idx, i) => (i === 0 ? `groups[${idx}]` : `.groups[${idx}]`))
      .join("")}.conditions[${conditionIndex}]`
  }

  const getGroupAndOrFieldPath = (): string => {
    if (currentPath.length === 1) {
      return `cards[${ruleIndex}].groups[${groupIndex}].andOr`
    }

    return (
      currentPath
        .slice(0, -1)
        .reduce((acc, idx, i) => (i === 0 ? `cards[${ruleIndex}].groups[${idx}]` : `${acc}.groups[${idx}]`), "") +
      `.groups[${groupIndex}].andOr`
    )
  }

  const getAddConditionFieldPath = (): string => {
    return `cards[${ruleIndex}].${currentPath
      .map((idx, i) => (i === 0 ? `groups[${idx}]` : `.groups[${idx}]`))
      .join("")}.conditions`
  }

  const getAddGroupFieldPath = (): string => {
    return `cards[${ruleIndex}].${currentPath
      .map((idx, i) => (i === 0 ? `groups[${idx}]` : `.groups[${idx}]`))
      .join("")}.groups`
  }

  const onAddCondition = (): void => {
    const fieldPath = getAddConditionFieldPath()
    setFieldValue(fieldPath, [...(group.conditions || []), { ...EMPTY_SCORECARD_CONDITION } as Rule])
  }

  const onAddGroup = (): void => {
    const newGroup: GroupConditions = {
      id: uuidv4(),
      andOr: "and",
      conditions: [EMPTY_SCORECARD_CONDITION as unknown as Rule, EMPTY_SCORECARD_CONDITION as unknown as Rule],
      groups: [],
    }
    const fieldPath = getAddGroupFieldPath()
    setFieldValue(fieldPath, [...(group.groups || []), newGroup])
  }

  return (
    <Grid display="flex" flexDirection="row" gap={1} mb={1} key={`group-${ruleIndex}-${pathKey}-${group.id}`}>
      <Grid item xs={3} md={2} lg={1.25}>
        {editMode || createMode ? (
          <Select
            type="text"
            value={group.andOr}
            handleChange={(e) => {
              const fieldPath = getGroupAndOrFieldPath()
              setFieldValue(fieldPath, e.target.value)
            }}
            fullWidth
            disabled={isSubmitting}
            options={LOGICAL_OPERATORS}
            hideDescription
          />
        ) : (
          <DataBlock value={group.andOr ?? "and"} />
        )}
      </Grid>

      <FieldArray name={getFieldArrayName()}>
        {({ remove }) => (
          <Grid container item justifyContent="flex-end" alignItems="flex-start" className={styles.groupBackground}>
            <Grid container item spacing={1}>
              {group.conditions?.map((condition, conditionIndex) => (
                <Grid item xs={12} key={`condition-${ruleIndex}-${pathKey}-${conditionIndex}-${condition?.id}`}>
                  <Condition
                    isScorecardLoading={false}
                    key={condition?.id}
                    ruleIndex={ruleIndex}
                    id={getConditionId(conditionIndex)}
                    index={conditionIndex}
                    isEditMode={Boolean(editMode)}
                    isCreateMode={createMode}
                    path={{ groupsIdx: currentPath, conditionIdx: conditionIndex }}
                    removeCondition={() =>
                      handleRemoveConditionByPath({
                        values,
                        setFieldValue,
                        setValues,
                        levelRemove: remove,
                        ruleIndex,
                        path: {
                          groupsIdx: currentPath,
                          conditionIdx: conditionIndex,
                        },
                      })
                    }
                  />
                </Grid>
              ))}

              {group.groups && group.groups.length > 0 && (
                <>
                  {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
                  <GroupRenderer
                    groups={group.groups}
                    ruleIndex={ruleIndex}
                    parentPath={currentPath}
                    editMode={editMode}
                    createMode={createMode}
                    currentDepth={currentDepth + 1}
                    maxDepth={maxDepth}
                  />
                </>
              )}

              {(editMode || createMode) && group.conditions && group.conditions.length > 0 && (
                <Grid container lg={12} mt={1} ml={1}>
                  <Button
                    size="regular"
                    onClick={(e) => onSetGroupAnchor(e?.currentTarget as HTMLElement)}
                    disabled={isSubmitting}
                  >
                    + {t("addRuleBtn")}
                  </Button>
                  <Menu
                    key={`basic-menu-${pathKey}`}
                    anchorEl={groupAnchor}
                    open={Boolean(groupAnchor)}
                    onClose={() => onSetGroupAnchor(null)}
                    menuMaxContent
                  >
                    <MenuItem
                      onClick={() => {
                        onAddCondition()
                        onSetGroupAnchor(null)
                      }}
                    >
                      <Grid container display={"flex"} gap={1} height={"20px"}>
                        <AddOutlinedIcon fontSize="small" htmlColor={theme.palette.base.text[1]} />
                        <Typography variant="a">{t("addRuleBtn")}</Typography>
                      </Grid>
                    </MenuItem>
                    {currentDepth < maxDepth - 1 && (
                      <MenuItem
                        onClick={() => {
                          onAddGroup()
                          onSetGroupAnchor(null)
                        }}
                      >
                        <Grid container display={"flex"} gap={1} height={"20px"}>
                          <AddBoxOutlinedIcon fontSize="small" htmlColor={theme.palette.base.text[1]} />
                          <Typography variant="a">{t("addGroupBtn")}</Typography>
                        </Grid>
                      </MenuItem>
                    )}
                  </Menu>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
      </FieldArray>
    </Grid>
  )
}

/**
 * Renders a group of conditions and nested groups for scorecard rules
 *
 * @component
 * @description
 * GroupRenderer is responsible for rendering condition groups in a scorecard rule.
 * It handles:
 * - Displaying and editing logical operators between conditions
 * - Rendering individual conditions within groups
 * - Managing nested groups up to a maximum depth
 * - Adding new conditions and groups
 * - Removing conditions
 *
 * The component uses Formik for form management and supports both view and edit modes.
 *
 * @example
 * ```tsx
 * <GroupRenderer
 *   groups={groupConditions}
 *   ruleIndex={0}
 *   parentPath={[0]}
 *   editMode={true}
 *   createMode={false}
 *   currentDepth={0}
 *   maxDepth={3}
 *   andOr={["and", "or"]}
 * />
 * ```
 */
export function GroupRenderer(props: GroupRendererProps): React.ReactElement {
  const { groups, ruleIndex, parentPath = [], editMode, createMode, currentDepth, maxDepth } = props

  const [groupAnchors, setGroupAnchors] = useState<{ [key: string]: HTMLElement | null }>({})

  if (currentDepth >= maxDepth) return <></>

  return (
    <Grid item xs>
      {groups.map((group, groupIndex) => {
        const currentPath = [...parentPath, groupIndex]
        const pathKey = currentPath.join("-")

        return (
          <GroupItem
            key={`group-${ruleIndex}-${pathKey}-${group.id}`}
            group={group}
            groupIndex={groupIndex}
            ruleIndex={ruleIndex}
            parentPath={parentPath}
            editMode={editMode}
            createMode={createMode}
            currentDepth={currentDepth}
            maxDepth={maxDepth}
            groupAnchor={groupAnchors[pathKey] || null}
            onSetGroupAnchor={(anchor) =>
              setGroupAnchors((prev) => ({
                ...prev,
                [pathKey]: anchor,
              }))
            }
          />
        )
      })}
    </Grid>
  )
}
